/**
 * skyTransformsSource — the source text of the injected `skyTransforms.js` and its filename.
 * The MATH & TRANSFORMS half of the former `skyHelpers.js` (its drawing/processing half is now
 * `skyDrawSource.js`). Kept as a string because it ships *inside the student's sketch* (it can't
 * import) and is eval'd verbatim by the AR viewer. Its transforms are unit-tested by evaluating
 * this string (skyTransformsSource.test.js), so there is one source of truth.
 *
 * Everything here is from scratch (textbook), in the canonical geo frame X=East, Y=DOWN, Z=South
 * (North = −Z, −Y = gravity-up). Y points DOWN because that is p5's own convention, and the AR
 * bridge now hands the sketch a frame in p5 space rather than compensating with a canvas flip
 * (see ar-viewer/src/cameraSync.js). Consequence: everything drawn geo-locked — text(), image(),
 * a billboarded 2D sketch — reads the same way up in AR as it does in the editor preview.
 * The three frame ops are called EXPLICITLY by the sketch (the bridge
 * no longer applies them behind the scenes); each expects a freshly push()ed (identity) frame:
 *   1. GEO-LOCK (applyGeoLock) — orients the current frame to gravity + true north using the
 *      alignment quaternion the bridge publishes as window.arGeoQuat.
 *   2. STAR-LOCK (lockStarsToSky) — self-contained: re-centers the dome on the observer
 *      (window.arCameraPos), geo-locks, then orients the fixed celestial sphere for the
 *      observer's latitude (geoOrientMatrix) and spins it about the pole-to-pole axis
 *      (poleAxis) by the current sidereal angle.
 *   3. APPEND-TO-STAR (appendToStar) — self-contained: sets up a camera-facing billboard pinned
 *      to a star's dome position (via billboardBasis/horizonDownVector) so a 2D sketch drawn
 *      afterwards appears attached to that star, upright toward the horizon.
 * It also carries the reverse direction: worldToScreenDepth() projects a point in the current
 * frame to screen pixels AND reports whether it is in front of the camera — the check p5's own
 * worldToScreen() omits, which is what otherwise draws labels for stars behind your head.
 *
 * The bridge publishes the data these read: window.arGeoQuat (geo→world rotation, already in p5
 * space), window.arCameraPos (observer world eye, p5 units and p5 space — so y is NEGATIVE above
 * the session origin) and window.arLocation (GPS fix). Location is automatic: lat/lon
 * default to window.arLocation, falling back to the SITE constant (and in the editor preview,
 * where those globals are absent, both ops degrade to a planetarium).
 *
 * p5 is reached via window.__stemartsP5 (the AR bridge's shared instance) or window (the editor's
 * global-mode p5), so the one file works in both.
 */

export const SKY_TRANSFORMS_FILENAME = 'skyTransforms.js';

export const SKY_TRANSFORMS_SOURCE = `// skyTransforms.js — celestial-sphere math & frame transforms. Generated by the Sketch menu.
// Frame: X=East, Y=DOWN, Z=South (North=-Z, up=-Y — p5's own Y-down convention).
// RA/Dec in DEGREES (J2000); math in radians.
(function () {
  var D2R = Math.PI / 180;
  var TWO_PI = Math.PI * 2;

  // Fallback site when no GPS fix is available (editor preview, or before a fix arrives).
  // The AR viewer supplies the real location via window.arLocation. (Placeholder: Taos, NM.)
  var SITE_LAT = 36.41;
  var SITE_LON = -105.57;

  function p5ctx() { return window.__stemartsP5 || window; }
  function autoLat(lat) {
    if (lat != null) return lat;
    return (window.arLocation && window.arLocation.lat != null) ? window.arLocation.lat : SITE_LAT;
  }
  function autoLon(lon) {
    if (lon != null) return lon;
    return (window.arLocation && window.arLocation.lon != null) ? window.arLocation.lon : SITE_LON;
  }

  // ── Pure transforms ────────────────────────────────────────────────────────────────

  // Fixed point on the celestial sphere for RA/Dec (degrees): equatorial unit p5.Vector.
  // Independent of location and time — bake these once (createStarGeometry, in skyDraw.js).
  window.raDecToVec = function (raDeg, decDeg) {
    var a = raDeg * D2R, d = decDeg * D2R, cd = Math.cos(d);
    return p5ctx().createVector(cd * Math.cos(a), cd * Math.sin(a), Math.sin(d));
  };

  // The celestial pole-to-pole axis (north celestial pole) in the geo frame:
  // altitude = latitude, due north. Unit p5.Vector (0, -sin lat, -cos lat) — the Y component is
  // NEGATIVE because the frame is Y-down, so "up by the latitude angle" is -Y.
  window.poleAxis = function (latDeg) {
    var phi = latDeg * D2R;
    return p5ctx().createVector(0, -Math.sin(phi), -Math.cos(phi));
  };

  // Rotation matrix (3x3, row-major) about a unit-axis p5.Vector by angle (radians), Rodrigues.
  window.rotateAboutAxis = function (axis, angle) {
    var x = axis.x, y = axis.y, z = axis.z;
    var c = Math.cos(angle), s = Math.sin(angle), t = 1 - c;
    return [
      c + x * x * t,       x * y * t - z * s,   x * z * t + y * s,
      y * x * t + z * s,   c + y * y * t,       y * z * t - x * s,
      z * x * t - y * s,   z * y * t + x * s,   c + z * z * t
    ];
  };

  // Orientation (3x3, row-major) of the fixed equatorial sphere into the geo frame at
  // LST=0 for a given latitude. Rows map (x,y,z)_equatorial -> (East, DOWN, South).
  // The middle row is the negated "Up" row of the textbook matrix — that single sign is what
  // expresses the right-handed equatorial sphere in p5's Y-down (left-handed) coordinates. It
  // makes det = -1, which is correct and NOT a bug: it is a coordinate change, not a mirror of
  // the sky. (Anything drawn upright on the dome uses billboardBasis, which stays det +1.)
  window.geoOrientMatrix = function (latDeg) {
    var phi = latDeg * D2R, sp = Math.sin(phi), cp = Math.cos(phi);
    return [
       0,  1,   0,
      -cp, 0,  -sp,
       sp, 0,  -cp
    ];
  };

  // Greenwich Mean Sidereal Time -> Local Sidereal Time (radians): how far the sky has
  // rotated about the pole for a Date + longitude. Textbook IAU polynomial.
  window.siderealAngleRad = function (date, lonDeg) {
    var JD = date.getTime() / 86400000 + 2440587.5;
    var Dd = JD - 2451545.0;
    var T = Dd / 36525.0;
    var deg = 280.46061837 + 360.98564736629 * Dd +
      0.000387933 * T * T - (T * T * T) / 38710000;
    deg = ((deg % 360) + 360) % 360;
    return ((deg * D2R + lonDeg * D2R) % TWO_PI + TWO_PI) % TWO_PI;
  };

  // Convenience: full horizontal direction (East, DOWN, South) of a star for LST + latitude,
  // as a p5.Vector. Altitude is computed as usual and then negated into the Y-down frame.
  window.starDirection = function (raDeg, decDeg, lst, latDeg) {
    var a = raDeg * D2R, d = decDeg * D2R, phi = latDeg * D2R;
    var H = lst - a, cd = Math.cos(d), sd = Math.sin(d);
    var east = -cd * Math.sin(H);
    var up = Math.sin(phi) * sd + Math.cos(phi) * cd * Math.cos(H);
    var north = Math.cos(phi) * sd - Math.sin(phi) * cd * Math.cos(H);
    return p5ctx().createVector(east, -up, -north);
  };

  function mat3mul(a, b) {
    var o = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (var r = 0; r < 3; r++) {
      for (var c = 0; c < 3; c++) {
        o[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
      }
    }
    return o;
  }

  // ── Frame ops (call these yourself, from a freshly push()ed frame) ───────────────────

  // GEO-LOCK. Rotate the current frame from p5's own frame into the geo frame
  // (X=East, Y=DOWN, Z=South). Content drawn after is world-anchored to gravity + true north.
  // Reads window.arGeoQuat — the geo→world alignment quaternion, which the AR bridge has already
  // conjugated into p5 space (see ar-viewer/src/cameraSync.js), so it can go straight into
  // p.rotate(). Absent in the editor preview, where this is a no-op and the sketch degrades to a
  // planetarium. SLAM is already gravity-aligned, so only the heading (yaw about up) is unknown.
  //
  // NO canvas/overlay compensation happens here any more: the AR camera transform is now built
  // so p5's Y-down projection agrees with the AR camera on its own. If AR content ever renders
  // upside-down or mirrored again, the place to look is cameraSync.js, not this function.
  window.applyGeoLock = function () {
    var p = p5ctx();
    var q = window.arGeoQuat; // geo → world (p5 space), {x,y,z,w}, or null
    if (q) {
      var w = Math.max(-1, Math.min(1, q.w)); // clamp for acos (no external clamp dep)
      var angle = 2 * Math.acos(w);
      var s = Math.sqrt(1 - w * w);
      if (s > 1e-6) p.rotate(angle, [q.x / s, q.y / s, q.z / s]);
    }
  };

  // STAR-LOCK (self-contained, from a freshly push()ed frame). Re-center the celestial dome on
  // the observer (window.arCameraPos), geo-lock the frame, then orient the fixed equatorial
  // sphere for the observer's latitude and spin it about the pole by the current sidereal angle —
  // so a dome/lines drawn in RA/Dec-baked coords show the real sky. Because the observer translate
  // happens in the world frame BEFORE the geo rotation, it uses the raw world eye directly (no
  // inversion). lat/lon default to the GPS fix (window.arLocation), else the SITE constant. Draw
  // stars/lines (skyDraw.js) after this, then pop().
  window.lockStarsToSky = function (lat, lon, date) {
    var p = p5ctx();
    var cp = window.arCameraPos;
    if (cp) p.translate(cp.x, cp.y, cp.z); // dome center = observer (world frame)
    window.applyGeoLock();
    var la = autoLat(lat), lo = autoLon(lon);
    var theta = window.siderealAngleRad(date || new Date(), lo);
    // +theta, not -theta: poleAxis is expressed in the Y-down frame, and reflecting a rotation
    // axis reverses the handedness of the turn about it (D·R(a,-θ)·D = R(D·a,+θ)). The sky still
    // turns westward — the sign lives in the axis, not in the physics.
    var spin = window.rotateAboutAxis(window.poleAxis(la), theta);
    var m = mat3mul(spin, window.geoOrientMatrix(la)); // 3x3 row-major: sketch = m * equatorial
    // p5 applyMatrix wants a column-major 4x4.
    p.applyMatrix(
      m[0], m[3], m[6], 0,
      m[1], m[4], m[7], 0,
      m[2], m[5], m[8], 0,
      0, 0, 0, 1
    );
  };

  // ── Append a 2D sketch to a star (billboard) ─────────────────────────────────────────

  // The "down" direction at a star (unit geo p5.Vector, X=East Y=DOWN Z=South): the tangent
  // toward the nearest horizon point = steepest descent in altitude = -(Up - (Up·s)s). At the
  // zenith (s ≈ Up) every horizon point is equidistant and this degenerates, so fall back to
  // the north point (0,0,-1) as the down vector. Input need not be pre-normalized.
  window.horizonDownVector = function (dir) {
    var p = p5ctx();
    var n = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z) || 1;
    var sx = dir.x / n, sy = dir.y / n, sz = dir.z / n;
    // Up - (Up·s)s, with Up = (0,-1,0) in this Y-down frame, so Up·s = -sy.
    var tx = sx * sy, ty = sy * sy - 1, tz = sz * sy;
    var tm = Math.sqrt(tx * tx + ty * ty + tz * tz);
    if (tm < 1e-6) return p.createVector(0, 0, -1); // zenith → north point
    return p.createVector(-tx / tm, -ty / tm, -tz / tm);
  };

  // Orientation (3x3, row-major) of a camera-facing billboard at a star, given the star's
  // geo-frame direction (X=East, Y=DOWN, Z=South). Columns are the images of local +X/+Y/+Z:
  //   +Z → -dir (faces the observer at the dome center), +Y → horizonDownVector projected ⊥ to
  //   +Z (p5's screen-down is +Y, so the sketch reads upright with its base toward the horizon),
  //   +X = +Y × +Z. det +1 — the billboard is a pure rotation of p5's own drawing frame, so a
  //   2D sketch (text and images included) lands on it upright and unmirrored. This is exactly
  //   the invariant the dome matrix above does NOT have, and does not need.
  window.billboardBasis = function (dir) {
    var dm = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z) || 1;
    var sx = dir.x / dm, sy = dir.y / dm, sz = dir.z / dm;
    var ezx = -sx, ezy = -sy, ezz = -sz; // +Z faces the camera
    var d = window.horizonDownVector(p5ctx().createVector(sx, sy, sz));
    var dez = d.x * ezx + d.y * ezy + d.z * ezz; // orthogonalize +Y against +Z
    var eyx = d.x - dez * ezx, eyy = d.y - dez * ezy, eyz = d.z - dez * ezz;
    var em = Math.sqrt(eyx * eyx + eyy * eyy + eyz * eyz) || 1;
    eyx /= em; eyy /= em; eyz /= em;
    var exx = eyy * ezz - eyz * ezy; // +X = +Y × +Z
    var exy = eyz * ezx - eyx * ezz;
    var exz = eyx * ezy - eyy * ezx;
    return [
      exx, eyx, ezx,
      exy, eyy, ezy,
      exz, eyz, ezz
    ];
  };

  // APPEND-TO-STAR (self-contained, from a freshly push()ed frame). Set up a transform so a 2D
  // sketch drawn afterwards appears billboarded onto the given star: pinned at the star's dome
  // position, always facing the camera (look-at from star → observer), and rolled so the sketch's
  // bottom points toward the nearest horizon (horizonDownVector). Accepts a star object with
  // {ra, dec} or raw ra/dec degrees; scale sizes the 2D sketch (default 1); radius is the dome
  // radius (default 300, matching createStarGeometry). lat/lon/date auto-resolve like
  // lockStarsToSky (GPS fix → SITE fallback → planetarium in the editor preview). Draw the sketch
  // after this call, then pop().
  window.appendToStar = function (a, b, c, d, e) {
    var p = p5ctx();
    var ra, dec, scale, radius, date;
    if (a && typeof a === 'object') {
      ra = a.ra; dec = a.dec; scale = b; radius = c; date = d;
    } else {
      ra = a; dec = b; scale = c; radius = d; date = e;
    }
    scale = (scale == null) ? 1 : scale;
    radius = (radius == null) ? 300 : radius;

    // Center the dome on the observer, then geo-lock — so the camera sits at the local origin
    // and the frame is the geo frame (X=East, Y=DOWN, Z=South), exactly as lockStarsToSky does.
    var cp = window.arCameraPos;
    if (cp) p.translate(cp.x, cp.y, cp.z);
    window.applyGeoLock();

    var lst = window.siderealAngleRad(date || new Date(), autoLon());
    var g = window.starDirection(ra, dec, lst, autoLat()); // unit geo direction to the star
    var m = window.billboardBasis(g);

    p.translate(g.x * radius, g.y * radius, g.z * radius); // out to the star on the dome
    p.applyMatrix( // orient the billboard (local axes → geo)
      m[0], m[3], m[6], 0,
      m[1], m[4], m[7], 0,
      m[2], m[5], m[8], 0,
      0, 0, 0, 1
    );
    p.scale(scale, scale, scale);
  };
// ── Projecting a point to the screen ────────────────────────────────────────────────

  // Where does a point in the CURRENT frame land on screen, and is it actually in front of us?
  // p5's own worldToScreen() answers only the first half, and that is the trap this exists for:
  // it divides by the homogeneous w without reporting its sign, and for a point BEHIND the camera
  // w is negative, so the division yields a mirrored but perfectly plausible on-screen position.
  // Label your stars with worldToScreen() and the ones behind your head get drawn too. The depth
  // returned here is that w, and isVisible is the sign test that suppresses them.
  //
  // Returns { x, y, z, depth, isVisible }:
  //   x, y     screen PIXELS, origin at the TOP-LEFT, y downward — the same convention p5's
  //            worldToScreen() uses, and the same one resetCamera() draws in, so a value from
  //            here can be drawn at literally these coordinates after a resetCamera().
  //   z        normalized depth: 0 at the near plane, 1 at the far plane.
  //   depth    distance in front of the camera along its view axis, in p5 world units (in AR,
  //            100 units = 1 metre — see P5_TO_AFRAME_SCALE in cameraSync.js).
  //   isVisible  depth > 0, i.e. in front of the camera. NOT a test of whether x/y are inside
  //            the canvas — a point off to the side is still "visible" by this measure.
  //
  // The matrix folds in the CURRENT model matrix as well as the view and projection, so this must
  // be called from inside the same push()/transform frame the point is expressed in — inside a
  // lockStarsToSky() frame for a star's dome position, for instance. Call it during draw(): before
  // the first frame there is no view matrix to read.
  window.worldToScreenDepth = function(v) {
    const P = window.__stemartsP5 || p5.instance;
    const r = P._renderer.getWorldToScreenMatrix().multiplyVec4(v.x, v.y, v.z, 1);
    const depth = r[3];
    return { x: r[0] / depth, y: r[1] / depth, z: r[2]/depth, depth, isVisible: depth > 0 };
  }

  
})();
`;
